import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client'
import { useHinweise } from '../hinweise/HinweisContext'

/**
 * Die Nachrichtenseite der Disposition.
 *
 * Links die Gesprächspartner, rechts der Verlauf — bei achtzig Fahrern plus acht
 * Dienstgruppen ist die Suche in der linken Spalte kein Beiwerk, sondern die
 * Voraussetzung dafür, dass die Seite benutzbar ist.
 *
 * Sortiert wird nach dem letzten Beitrag. Wer noch nie angeschrieben wurde, steht
 * darunter in seiner natürlichen Ordnung: Er soll auffindbar sein, aber nicht die
 * laufenden Gespräche verdrängen.
 */

interface Partner {
  id: number | null
  art: 'FAHRER' | 'TEAM' | 'ALLE' | 'WERKSTATT'
  stelle: 'DISPOSITION' | 'WERKSTATT'
  bezugId: number | null
  schluessel: string
  name: string
  zusatz: string
  teilnehmer: number
  ohneApp: number
  vorschau: string | null
  letzterBeitragAm: string | null
  ungelesen: number
}

interface Beitrag {
  id: number
  text: string
  wichtig: boolean
  bestaetigungNoetig: boolean
  gesendetAm: string
  absender: string
  vomBetrieb: boolean
  gelesenVon: number
  bestaetigtVon: number
  teilnehmer: number
}

interface Verlauf {
  partner: Partner
  beitraege: Beitrag[]
}

function zeitpunkt(iso: string): string {
  const d = new Date(iso)
  const heute = new Date()
  const gleicherTag = d.toDateString() === heute.toDateString()
  return gleicherTag
    ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/** „vor 3 Min." liest sich in einer Liste schneller als eine Uhrzeit. */
function seit(iso: string | null): string {
  if (!iso) return ''
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 1) return 'gerade eben'
  if (min < 60) return `vor ${min} Min.`
  if (min < 24 * 60) return `vor ${Math.floor(min / 60)} Std.`
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
}

export function Gespraeche() {
  const [partner, setPartner] = useState<Partner[]>([])
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)
  const [verlauf, setVerlauf] = useState<Verlauf | null>(null)
  const [suche, setSuche] = useState('')
  const [text, setText] = useState('')
  const [wichtig, setWichtig] = useState(false)
  const [bestaetigung, setBestaetigung] = useState(false)
  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const ende = useRef<HTMLDivElement>(null)
  const { aktualisiere: hinweiseNachziehen } = useHinweise()

  const ladePartner = useCallback(async () => {
    try {
      setPartner(await api.get<Partner[]>('/api/gespraeche'))
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Liste ließ sich nicht laden.')
    }
  }, [])

  useEffect(() => {
    void ladePartner()
  }, [ladePartner])

  /** Der Verlauf des gewählten Partners. */
  const ladeVerlauf = useCallback(async (p: Partner) => {
    setLaedt(true)
    setFehler(null)
    try {
      // Die Stelle mitgeben: Ein Disponent kann auch einen Werkstattfaden oeffnen und
      // mitlesen. Ohne sie bekaeme er immer seinen eigenen Faden mit demselben Fahrer.
      const pfad = `/api/gespraeche/${p.art.toLowerCase()}/${p.bezugId ?? 0}?stelle=${p.stelle}`
      setVerlauf(await api.get<Verlauf>(pfad))
      // Das Oeffnen vermerkt die Beitraege als gelesen. Ohne diesen Anstoss stuende das
      // Zeichen am Menuepunkt noch bis zum naechsten Takt — zwanzig Sekunden, in denen
      // jemand sucht, was er doch gerade gelesen hat.
      hinweiseNachziehen()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Verlauf ließ sich nicht laden.')
    } finally {
      setLaedt(false)
    }
  }, [hinweiseNachziehen])

  // Nach jedem Laden ans Ende springen — ein Chat wird von unten gelesen.
  useEffect(() => {
    if (verlauf) ende.current?.scrollIntoView({ block: 'end' })
  }, [verlauf])

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase()
    if (!s) return partner
    return partner.filter(
      (p) => p.name.toLowerCase().includes(s) || p.zusatz.toLowerCase().includes(s),
    )
  }, [partner, suche])

  const aktiv = partner.find((p) => p.schluessel === gewaehlt) ?? null

  async function waehle(p: Partner) {
    setGewaehlt(p.schluessel)
    setText('')
    setWichtig(false)
    setBestaetigung(false)
    await ladeVerlauf(p)
    // Ungelesenes ist mit dem Öffnen erledigt — die Liste soll das sofort zeigen.
    void ladePartner()
  }

  async function senden() {
    if (!aktiv || !text.trim()) return
    setLaedt(true)
    setFehler(null)
    try {
      const pfad = `/api/gespraeche/${aktiv.art.toLowerCase()}/${aktiv.bezugId ?? 0}`
      await api.post(pfad, { text: text.trim(), wichtig, bestaetigungNoetig: bestaetigung })
      setText('')
      setWichtig(false)
      setBestaetigung(false)
      await ladeVerlauf(aktiv)
      await ladePartner()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Nachricht ging nicht raus.')
    } finally {
      setLaedt(false)
    }
  }

  return (
    <div className="chat">
      {/* ---------------------------------------------------- Linke Spalte */}
      <aside className="chat-liste">
        <div className="chat-suche">
          <input
            type="search"
            placeholder="Fahrer oder Dienstgruppe suchen…"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            aria-label="Gesprächspartner suchen"
          />
        </div>

        <div className="chat-partner">
          {gefiltert.length === 0 && (
            <p className="leer">Kein Treffer für „{suche}“.</p>
          )}
          {gefiltert.map((p) => (
            <button
              key={p.schluessel}
              type="button"
              className={
                'chat-partner-zeile' +
                (p.schluessel === gewaehlt ? ' aktiv' : '') +
                (p.letzterBeitragAm ? '' : ' stumm')
              }
              onClick={() => void waehle(p)}
            >
              <span className={'chat-symbol art-' + p.art.toLowerCase()}>
                {p.art === 'ALLE' ? '★' : p.art === 'TEAM' ? '❏' : p.name.charAt(0)}
                {/*
                  Der Punkt sagt, ob die Nachricht ueberhaupt ankommt: Ein Fahrer ohne
                  gekoppeltes Geraet sieht sie erst, wenn ihm jemand davon erzaehlt.
                  Bei Gruppen steht die Zahl darunter, hier genuegt ja/nein.
                */}
                {p.art === 'FAHRER' && (
                  <span
                    className={'chat-punkt' + (p.ohneApp === 0 ? ' erreichbar' : '')}
                    title={p.ohneApp === 0 ? 'App gekoppelt' : 'Kein Gerät gekoppelt'}
                  />
                )}
              </span>
              <span className="chat-partner-text">
                <span className="chat-partner-kopf">
                  <strong>{p.name}</strong>
                  {p.stelle === 'WERKSTATT' && (
                    <span className="chat-stelle" title="Schriftwechsel der Werkstatt">
                      Werkstatt
                    </span>
                  )}
                  <span className="chat-zeit">{seit(p.letzterBeitragAm)}</span>
                </span>
                <span className="chat-partner-fuss">
                  <span className="chat-vorschau">
                    {p.vorschau ?? p.zusatz}
                    {p.art !== 'FAHRER' && p.ohneApp > 0 && (
                      <span className="chat-ohne-app"> · {p.ohneApp} ohne App</span>
                    )}
                  </span>
                  {p.ungelesen > 0 && <span className="chat-zaehler">{p.ungelesen}</span>}
                </span>
              </span>
            </button>
          ))}
        </div>
      </aside>

      {/* ---------------------------------------------------- Rechte Spalte */}
      <section className="chat-verlauf">
        {!aktiv && (
          <div className="chat-leer">
            <p>Links einen Fahrer, eine Dienstgruppe oder „An alle Fahrer“ wählen.</p>
          </div>
        )}

        {aktiv && (
          <>
            <header className="chat-kopf">
              <div>
                <h2>
                  {aktiv.name}
                  {aktiv.stelle === 'WERKSTATT' && (
                    <span className="chat-stelle gross">Werkstatt</span>
                  )}
                </h2>
                <p className="chat-kopf-zusatz">
                  {aktiv.zusatz}
                  {aktiv.ohneApp > 0 && (
                    <>
                      {' · '}
                      <span className="chat-warnung">
                        {aktiv.art === 'FAHRER'
                          ? 'kein Gerät gekoppelt — sieht die Nachricht erst im Web'
                          : `${aktiv.ohneApp} ohne App`}
                      </span>
                    </>
                  )}
                  {aktiv.art === 'FAHRER' && aktiv.ohneApp === 0 && (
                    <>{' · '}<span className="chat-erreichbar">App gekoppelt</span></>
                  )}
                </p>
              </div>
            </header>

            {fehler && <div className="meldung fehler">{fehler}</div>}

            <div className="chat-blasen">
              {verlauf?.beitraege.length === 0 && (
                <p className="leer">Noch keine Nachricht. Schreiben Sie die erste.</p>
              )}
              {verlauf?.beitraege.map((b) => (
                <article
                  key={b.id}
                  className={'blase' + (b.vomBetrieb ? ' eigen' : ' fremd')}
                >
                  {!b.vomBetrieb && <span className="blase-absender">{b.absender}</span>}
                  {b.wichtig && <span className="blase-wichtig">Wichtig</span>}
                  <p className="blase-text">{b.text}</p>
                  <footer className="blase-fuss">
                    <span>{zeitpunkt(b.gesendetAm)}</span>
                    {b.vomBetrieb && (
                      <span className="blase-nachweis">
                        {b.bestaetigungNoetig
                          ? `${b.bestaetigtVon} von ${b.teilnehmer} bestätigt`
                          : `${b.gelesenVon} von ${b.teilnehmer} gelesen`}
                      </span>
                    )}
                  </footer>
                </article>
              ))}
              <div ref={ende} />
            </div>

            {aktiv.stelle === 'WERKSTATT' && (
              <p className="chat-hinweis">
                Das ist der Schriftwechsel der Werkstatt. Ihre Antwort erscheint im Faden
                der Disposition — der Fahrer sieht beide getrennt.
              </p>
            )}

            <footer className="chat-eingabe">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sendet, Umschalt+Enter macht einen Absatz — wie überall sonst.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    void senden()
                  }
                }}
                placeholder={`Nachricht an ${aktiv.name}…`}
                rows={2}
              />
              <div className="chat-eingabe-fuss">
                <label>
                  <input
                    type="checkbox"
                    checked={wichtig}
                    onChange={(e) => setWichtig(e.target.checked)}
                  />
                  Wichtig
                </label>
                <label title="Der Fahrer muss ausdrücklich bestätigen — der Nachweis für eine Dienstanweisung.">
                  <input
                    type="checkbox"
                    checked={bestaetigung}
                    onChange={(e) => setBestaetigung(e.target.checked)}
                  />
                  Bestätigung verlangen
                </label>
                <button
                  type="button"
                  className="knopf knopf-primaer"
                  onClick={() => void senden()}
                  disabled={laedt || !text.trim()}
                >
                  Senden
                </button>
              </div>
            </footer>
          </>
        )}
      </section>
    </div>
  )
}
